import { Component } from '@theme/component';

/**
 * Mobile sticky add-to-cart bar. Proxies to the real product form on the
 * page so form validation, variant selection and quantity all work.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} button
 * @property {HTMLElement} buttonText
 * @property {HTMLElement} [priceTarget]
 *
 * @extends {Component<Refs>}
 */
export class EfsStickyAtc extends Component {
  requiredRefs = ['button', 'buttonText'];

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #animTimeout;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {IntersectionObserver | undefined} */
  #observer;

  get productId() {
    return this.dataset.productId ?? '';
  }

  get alwaysShow() {
    return this.dataset.alwaysShow === 'true';
  }

  get addToCartText() {
    return this.dataset.addToCartText ?? '';
  }

  get soldOutText() {
    return this.dataset.soldOutText ?? '';
  }

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const realAtc = this.#getRealForm()?.querySelector('.add-to-cart-button');
    if (!this.alwaysShow && realAtc) {
      this.#observer = new IntersectionObserver(
        ([entry]) => {
          this.hidden = entry.isIntersecting;
        },
        { threshold: 0.1 }
      );
      this.#observer.observe(realAtc);
    }

    document.addEventListener('cart:update', this.#handleCartUpdate, { signal });
    document.addEventListener('variant:selected', this.#handleVariantSelected, { signal });
    // Capture phase: run before product-price's section-level bubble listener,
    // which would otherwise move the priceContainer out of the event's HTML fragment.
    document.addEventListener('variant:update', this.#handleVariantUpdate, { signal, capture: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#observer?.disconnect();
    clearTimeout(this.#animTimeout);
  }

  /**
   * Wired via `on:click="/handleClick"` on the button.
   */
  handleClick() {
    const realForm = this.#getRealForm();
    const form = realForm?.querySelector('form');
    const submitBtn = form?.querySelector('.add-to-cart-button');
    if (!form || !(submitBtn instanceof HTMLButtonElement) || submitBtn.disabled) return;

    this.#playFlyToCart(realForm);
    // Triggers HTML5 validation + the component's own submit handler,
    // which reads FormData (including the PDP's quantity input).
    form.requestSubmit();
  }

  #getRealForm() {
    return /** @type {HTMLElement | null} */ (
      document.querySelector(`product-form-component[data-product-id="${this.productId}"]`)
    );
  }

  /**
   * @param {HTMLElement | null} realForm
   */
  #playFlyToCart(realForm) {
    if (!realForm) return;
    const atcComp = realForm.querySelector('add-to-cart-component');
    if (!(atcComp instanceof HTMLElement) || atcComp.dataset.addToCartAnimation !== 'true') return;

    const image = atcComp.dataset.productVariantMedia;
    const cartIcon = document.querySelector('.header-actions__cart-icon');
    if (!image || !cartIcon) return;

    const flyEl = /** @type {any} */ (document.createElement('fly-to-cart'));
    flyEl.style.setProperty('background-image', `url(${image})`);
    flyEl.source = this.refs.button;
    flyEl.destination = cartIcon;
    document.body.appendChild(flyEl);
  }

  #handleCartUpdate = (/** @type {Event} */ event) => {
    const detail = /** @type {any} */ (event).detail;
    if (detail?.data?.didError) return;
    if (String(detail?.data?.productId) !== this.productId) return;

    clearTimeout(this.#animTimeout);
    this.refs.button.classList.add('atc-added');
    this.#animTimeout = setTimeout(() => this.refs.button.classList.remove('atc-added'), 2000);
  };

  #handleVariantSelected = (/** @type {Event} */ event) => {
    // variant:selected traegt nur die OptionValue-ID, keinen product_id.
    // Daher filtern wir nach DOM-Ursprung: PDP-Haupt-Picker liegt nicht in
    // product-card oder quick-add-dialog.
    // Pickers fuer andere Produkte (Produkt-Popup, Warenkorb-Empfehlung) ebenfalls
    // ignorieren — sonst bleibt is-fetching haengen, weil das folgende
    // variant:update eine fremde productId traegt.
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (target.closest('product-card, quick-add-dialog')) return;
    const picker = target.closest('variant-picker');
    if (picker instanceof HTMLElement && picker.dataset.productId !== this.productId) return;
    this.classList.add('is-fetching');
  };

  #handleVariantUpdate = (/** @type {Event} */ event) => {
    const detail = /** @type {any} */ (event).detail;
    if (String(detail?.data?.productId) !== this.productId) return;

    this.classList.remove('is-fetching');

    const resource = detail?.resource;
    if (!resource) return;

    const html = detail?.data?.html;
    if (html && this.refs.priceTarget) {
      const source = html.querySelector(`product-price[data-product-id="${this.productId}"] .price-row`);
      if (source) this.refs.priceTarget.innerHTML = source.innerHTML;
    }

    const unavailable = resource.available === false;
    this.refs.button.disabled = unavailable;
    this.refs.buttonText.textContent = unavailable ? this.soldOutText : this.addToCartText;
  };
}

if (!customElements.get('efs-sticky-atc')) {
  customElements.define('efs-sticky-atc', EfsStickyAtc);
}
